/**
 * Pack-oriented commercial capabilities for the About modal.
 * Keep pack_id / feature_keys aligned with controlplaneapi commercialPackCatalog.
 */
export type AboutCommercialPack = {
  pack_id: string;
  title: string;
  description: string;
  feature_keys: string[];
};

/** Custom (P3) SKU placeholders — display only until CP-PACK-S3 catalog entry. */
export const CUSTOM_SKU_PLACEHOLDERS = [
  {
    feature_key: "ARCHIVE_STORAGE",
    display_name: "Long-Term Archive Storage",
    description:
      "Usage/GB long-term archive storage. Custom a-la-carte only (not a standard pack).",
  },
] as const;

export const ABOUT_COMMERCIAL_PACKS: readonly AboutCommercialPack[] = [
  {
    pack_id: "pack.continuous_monitoring",
    title: "Continuous ICU & EMU Monitoring",
    description:
      "ICU/EMU continuous monitoring with signal quality and multi-reader collaboration.",
    feature_keys: [
      "ICU_EMU_MONITORING",
      "SIGNAL_QUALITY_MONITORING",
      "REAL_TIME_COLLABORATION",
    ],
  },
  {
    pack_id: "pack.care_continuum",
    title: "Care Continuum",
    description: "Telehealth sessions plus remote assistance for distributed care.",
    feature_keys: ["TELEHEALTH", "REMOTE_ASSISTANCE"],
  },
  {
    pack_id: "pack.qeeg",
    title: "Quantitative EEG (qEEG)",
    description: "Quantitative EEG analysis and lab insights workflows.",
    feature_keys: ["STATISTICAL_ANALYSIS", "ADVANCED_ANALYTICS"],
  },
  {
    pack_id: "pack.ai_reporting",
    title: "AI Clinical Reporting",
    description: "Clinical copilot and AI report drafting with provenance.",
    feature_keys: ["AI_CLINICAL_COPILOT", "AI_REPORT_DRAFT"],
  },
  {
    pack_id: "pack.quality_training",
    title: "Quality Assurance & Training",
    description: "Quality assurance suite and clinical training portal.",
    feature_keys: ["QA_DASHBOARD", "TRAINING"],
  },
  {
    pack_id: "pack.visualization",
    title: "3D EEG Visualization & Mapping",
    description: "3D EEG visualization and source localization (ESI).",
    feature_keys: ["3D_VISUALIZATION", "SOURCE_LOCALIZATION"],
  },
  {
    pack_id: "pack.support_ai",
    title: "Support AI Suite",
    description: "AI service desk, support calls, and support-agent copilot.",
    feature_keys: ["AI_SERVICE_DESK", "AI_SUPPORT_CALLS", "AI_AGENT_COPILOT"],
  },
  {
    pack_id: "pack.enterprise_trust",
    title: "Enterprise Trust & Private AI",
    description: "AI compliance, private model routing, and priority support.",
    feature_keys: [
      "AI_COMPLIANCE",
      "AI_PRIVATE_MODEL_ROUTING",
      "PRIORITY_SUPPORT",
    ],
  },
  {
    pack_id: "pack.neonatal",
    title: "Neonatal EEG Specialty",
    description: "Neonatal specialty workflows with signal quality monitoring.",
    feature_keys: ["NEONATAL_EEG", "SIGNAL_QUALITY_MONITORING"],
  },
  {
    pack_id: "pack.ambulatory",
    title: "Ambulatory / Home EEG",
    description: "Ambulatory and home EEG intake with telehealth continuity.",
    feature_keys: ["AMBULATORY_EEG", "TELEHEALTH"],
  },
];

export function cataloguedFeatureKeysForAbout(): string[] {
  const keys = new Set<string>();
  for (const pack of ABOUT_COMMERCIAL_PACKS) {
    for (const key of pack.feature_keys) {
      keys.add(key);
    }
  }
  return Array.from(keys).sort();
}

/**
 * A pack is enabled when every included feature key is available.
 */
export function resolveEnabledCommercialPacks(
  availability: Record<string, { available?: boolean } | undefined>,
): AboutCommercialPack[] {
  return ABOUT_COMMERCIAL_PACKS.filter((pack) =>
    pack.feature_keys.every((key) => availability[key]?.available),
  );
}
